import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '..';
import type { WorkatoConfigurationItem } from '@server/workato-documentation';
import { WorkatoElementType } from '@server/workato-documentation';
import {
  WORKATO_TABS_STORAGE_KEY,
  loadTabsForOrg,
  saveTabsForOrg,
} from '../../lib/documentationTabsLocalStorage';

type ElementsByType = Partial<Record<string, WorkatoConfigurationItem[]>>;

export const WORKATO_DEFAULT_TAB = WorkatoElementType.RECIPE;

export const WORKATO_DEFAULT_REMOVABLE_TABS: WorkatoElementType[] = [
  WorkatoElementType.PROJECT,
  WorkatoElementType.CONNECTION,
];

export const WORKATO_INITIAL_TABS: WorkatoElementType[] = [
  WORKATO_DEFAULT_TAB,
  ...WORKATO_DEFAULT_REMOVABLE_TABS,
];

export interface WorkatoDocumentationState {
  elementsByOrg: {
    [crmOrgId: string]: ElementsByType;
  };
  loadingByOrg: {
    [crmOrgId: string]: Partial<Record<string, boolean>>;
  };
  activeParentType: WorkatoElementType;
  openTabs: WorkatoElementType[];
}

const initialState: WorkatoDocumentationState = {
  elementsByOrg: {},
  loadingByOrg: {},
  activeParentType: WORKATO_DEFAULT_TAB,
  openTabs: [...WORKATO_INITIAL_TABS],
};

export const workatoDocumentationSlice = createSlice({
  name: 'workatoDocumentation',
  initialState,
  reducers: {
    setWorkatoElements: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        tabType: string;
        elements: WorkatoConfigurationItem[];
      }>,
    ) => {
      const { crmOrgId, tabType, elements } = action.payload;
      if (!state.elementsByOrg[crmOrgId]) {
        state.elementsByOrg[crmOrgId] = {};
      }
      state.elementsByOrg[crmOrgId][tabType] = elements;
    },

    setWorkatoLoading: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        tabType: string;
        isLoading: boolean;
      }>,
    ) => {
      const { crmOrgId, tabType, isLoading } = action.payload;
      if (!state.loadingByOrg[crmOrgId]) {
        state.loadingByOrg[crmOrgId] = {};
      }
      state.loadingByOrg[crmOrgId][tabType] = isLoading;
    },

    clearWorkatoElements: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      delete state.elementsByOrg[action.payload.crmOrgId];
      delete state.loadingByOrg[action.payload.crmOrgId];
    },

    setWorkatoActiveParentType: (state, action: PayloadAction<WorkatoElementType>) => {
      state.activeParentType = action.payload;
    },

    loadWorkatoTabsForOrg: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const stored = loadTabsForOrg<WorkatoElementType[]>(
        WORKATO_TABS_STORAGE_KEY,
        action.payload.crmOrgId,
      );
      if (stored?.length) {
        const withDefaults = [
          WORKATO_DEFAULT_TAB,
          ...stored.filter((t) => t !== WORKATO_DEFAULT_TAB),
        ];
        state.openTabs = withDefaults;
        state.activeParentType = withDefaults[0];
      }
    },

    addWorkatoTab: (
      state,
      action: PayloadAction<{ parentType: WorkatoElementType; crmOrgId: string }>,
    ) => {
      const { parentType, crmOrgId } = action.payload;
      if (!state.openTabs.includes(parentType)) {
        state.openTabs.push(parentType);
      }
      state.activeParentType = parentType;
      const userTabs = state.openTabs.filter((t) => t !== WORKATO_DEFAULT_TAB);
      saveTabsForOrg(WORKATO_TABS_STORAGE_KEY, crmOrgId, userTabs);
    },

    removeWorkatoTab: (
      state,
      action: PayloadAction<{ parentType: WorkatoElementType; crmOrgId: string }>,
    ) => {
      const { parentType, crmOrgId } = action.payload;
      if (parentType === WORKATO_DEFAULT_TAB) return;
      state.openTabs = state.openTabs.filter((t) => t !== parentType);
      if (state.activeParentType === parentType) {
        state.activeParentType = state.openTabs[state.openTabs.length - 1] ?? WORKATO_DEFAULT_TAB;
      }
      const userTabs = state.openTabs.filter((t) => t !== WORKATO_DEFAULT_TAB);
      saveTabsForOrg(WORKATO_TABS_STORAGE_KEY, crmOrgId, userTabs);
    },
  },
});

export const {
  setWorkatoElements,
  setWorkatoLoading,
  clearWorkatoElements,
  setWorkatoActiveParentType,
  addWorkatoTab,
  removeWorkatoTab,
  loadWorkatoTabsForOrg,
} = workatoDocumentationSlice.actions;

export const selectWorkatoElements =
  (crmOrgId: string, tabType: string) => (state: RootState) =>
    state[workatoDocumentationSlice.name]?.elementsByOrg[crmOrgId]?.[tabType];

export const selectWorkatoLoading =
  (crmOrgId: string, tabType: string) => (state: RootState) =>
    state[workatoDocumentationSlice.name]?.loadingByOrg[crmOrgId]?.[tabType] ?? false;

export const selectAllWorkatoElements = (crmOrgId: string) => (state: RootState) =>
  state[workatoDocumentationSlice.name]?.elementsByOrg[crmOrgId];

export const selectWorkatoActiveParentType = (state: RootState) =>
  state[workatoDocumentationSlice.name]?.activeParentType ?? WORKATO_DEFAULT_TAB;

export const selectWorkatoOpenTabs = (state: RootState) =>
  state[workatoDocumentationSlice.name]?.openTabs ?? [...WORKATO_INITIAL_TABS];

export default workatoDocumentationSlice.reducer;
